import type { TenantRoleCode } from "@planerius/contracts/cabinet-auth"

/**
 * 4-Г4 «Доступ приостановлен» — что сказать и что предложить, отдельно от разметки.
 *
 * ОДИН ТЕКСТ НА ВСЕХ ВРЁТ ХОТЯ БЫ ОДНОМУ, и это главное решение файла:
 *
 *   — владельцу «обратитесь к владельцу салона» — насмешка, и он идёт в поддержку злым;
 *   — администратору «оплатите» — указание сделать то, чего он сделать не может: карта
 *     не его, и доступа к оплате у него нет;
 *   — мастеру не нужно ни то, ни другое: ему нужно знать, что записи не пропали
 *     и что решают это без него.
 *
 * АРХИВ — НЕ ПРИОСТАНОВКА. Приостановку снимают оплатой, архив — только обращением
 * к нам. Обещать владельцу «оплатите» в архиве значит послать его платить за то,
 * что оплатой не решается.
 */

export type TenantAccessStatus = "suspended" | "archived"

export interface SuspendedView {
  readonly title: string
  readonly text: string
  /** Главное действие. null — этому человеку решать нечего, и кнопки не будет. */
  readonly action: { readonly kind: "pay" | "support"; readonly label: string } | null
  /**
   * Можно ли выгрузить базу отсюда.
   *
   * ВЫГРУЗКА РАБОТАЕТ ВСЕГДА — но показывать кнопку тому, у кого нет права, значит
   * обещать невозможное: право `clients.export` есть у владельца и управляющего,
   * а у мастера его нет по умолчанию.
   */
  readonly canExport: boolean
}

export function buildSuspendedView(input: {
  readonly status: TenantAccessStatus
  readonly roleCode: TenantRoleCode | null
  readonly canExport: boolean
  readonly tenantName: string
  /**
   * Формирует ли сервер файлы выгрузки ВООБЩЕ.
   *
   * Тексты обещали владельцу «клиентскую базу можно выгрузить прямо сейчас», а обработчик
   * выгрузок не подключён ни в одном окружении: нажатие отвечало 503. Обещание в главном
   * абзаце экрана дороже, чем в списке ниже, — именно его человек читает первым, и именно
   * оно превращается в «базу не отдают за неуплату».
   */
  readonly exportAvailable: boolean
}): SuspendedView {
  const owner = input.roleCode === "owner"
  const manager = input.roleCode === "admin" || input.roleCode === "manager"
  const exportPhrase = input.exportAvailable
    ? "Клиентскую базу можно выгрузить прямо сейчас."
    : "Клиентская база на месте и остаётся вашей; файл выгрузки мы пока не формируем."

  if (input.status === "archived") {
    return {
      title: "Салон в архиве",
      text: owner
        ? `«${input.tenantName}» переведён в архив. Данные сохранены полностью — напишите нам, и мы вернём доступ. ${exportPhrase}`
        : `«${input.tenantName}» переведён в архив. Данные сохранены; вернуть доступ может владелец салона через нашу поддержку.`,
      action: owner || manager ? { kind: "support", label: "Написать в поддержку" } : null,
      canExport: input.canExport,
    }
  }

  return {
    title: "Доступ приостановлен",
    text: owner
      ? `Работа кабинета «${input.tenantName}» приостановлена из-за неоплаты. Оплата снимает ограничение сразу. Записи, клиенты и график на месте — ничего не удалено. ${exportPhrase}`
      : manager
        ? `Работа кабинета «${input.tenantName}» приостановлена из-за неоплаты. Снять ограничение может владелец салона — скажите ему. Если его нет на связи, напишите нам: мы подскажем, что делать. Записи и клиенты на месте.`
        : `Работа кабинета «${input.tenantName}» приостановлена. Это решают владелец салона и администратор — записи и клиенты никуда не делись, ваш график сохранён.`,
    action: owner
      ? { kind: "pay", label: "Оплатить" }
      : manager
        ? { kind: "support", label: "Написать в поддержку" }
        : null,
    canExport: input.canExport,
  }
}

/**
 * Что по-прежнему работает — перечисляется СЛОВАМИ.
 *
 * Человек, увидевший закрытый кабинет, первым делом думает, что потерял данные. Пока
 * ему не сказали обратное, он звонит; сказали — не звонит. Это и есть смысл списка.
 *
 * СПИСОК ЗАВИСИТ ОТ ТОГО, ЧТО РАБОТАЕТ НА САМОМ ДЕЛЕ. Он был постоянным и обещал
 * выгрузку клиентской базы, которой сервер не формирует ни в одном окружении:
 * обработчик выгрузок не подключён, и каждое нажатие отвечало 503. Обещание, которого
 * мы не держим, здесь дороже всего: человек читает «базу не отдают за неуплату» —
 * то есть ровно то, чего мы этим экраном и не хотели.
 */
export function suspendedStillWorks(input: { readonly exportAvailable: boolean }): readonly string[] {
  return [
    "Напоминания уже записанным клиентам продолжают уходить — по бесплатным каналам.",
    "Онлайн-запись на вашем адресе выключена: новые записи не появятся.",
    "Данные не удалены и не переданы никому.",
    input.exportAvailable
      ? "Выгрузка клиентской базы работает: она ваша и остаётся вашей."
      : "База остаётся вашей: она не удалена и никому не передана. Файл выгрузки мы пока не формируем — это наша недоделка, а не удержание за неоплату.",
  ]
}

/** Причина выгрузки в аудит: она обязательна, и подставлять «—» нельзя. */
export const SUSPENDED_EXPORT_PURPOSE =
  "Выгрузка клиентской базы при ограниченном доступе к кабинету"
